/* Which rule bindings appear to apply to one dataset.
 *
 * **An approximation, and the screen must not present it as more than that.** `core.rules` owns
 * selector resolution: a binding's selectors are matched against a dataset's qualified name, its
 * tags and its column roles, and the platform's answer is the one an execution actually uses. No
 * route exposes "the bindings that resolve to this dataset", so a screen wanting to show them either
 * approximates or shows nothing.
 *
 * This approximates on `name_glob` alone, because that is the selector a steward writes when they
 * mean one table, and it can therefore only **under**-report — a binding that reaches this dataset
 * by tag or column role is missed. Under-reporting is the safe direction for a panel headed "rules
 * on this dataset": a missing row sends somebody to the Rules screen, where the truth is, whereas an
 * invented row would have them believe a table is covered when it is not.
 *
 * The filter this replaced compared a selector's kind to `'dataset_id'`, which is not a member of
 * `SelectorKind` — so the panel had always been empty, and a `String()` around an already-string
 * value was hiding the type error that would have said so.
 */

/** Whether a `name_glob` selector pattern matches a dataset's qualified name.
 *
 * `*` is the only wildcard, matching any run of characters including none — the same shape
 * `sql_builders` renders into a `LIKE`, where `%` plays that role. Everything else in the pattern is
 * literal, so a `.` in `sales.orders` is a dot and not "any character": treating a pattern as a
 * regex would make `sales.orders` match `salesXorders`, and a steward writing a table name does not
 * expect to have escaped anything.
 */
export function nameGlobMatches(pattern: string, qualifiedName: string): boolean {
  if (pattern === '') return false;
  const escaped = pattern.replace(/[.*+?^${}()|[\]\\]/g, (character) =>
    character === '*' ? ' ' : `\\${character}`,
  );
  return new RegExp(`^${escaped.split(' ').join('.*')}$`).test(qualifiedName);
}
